import { Map, List } from 'immutable'
import { delay } from 'bluebird'
import toString from 'lodash/toString'
import isFinite from 'lodash/isFinite'
import { getMedia } from '../../media'
import {
  GET_MEDIA_ASSETS_DATA_ASSET,
  PUBLISH_MEDIA_ASSETS_DATA,
  PUBLISH_MEDIA_ASSETS_DATA_ASSET,
  UNPUBLISH_MEDIA_ASSETS_DATA_ASSET,
  GET_MEDIA_ASSET_BY_ID,
  setMediaAssetsDataAsset,
  setMediaAssetsDataAssetProcessing,
  setMediaAssetsMessage,
  publishMediaAssetsDataAsset,
} from './actions'
import { MEDIA_ASSETS_PROVIDER_AWS_S3 } from '../providers'
import {
  upsertMediaAsset,
  deleteMediaAsset,
  getMediaAssetById,
  getMediaAssetsByMediaId,
} from '..'

/**
 * Create a generic mediaAsset message to display in the UI, useful
 * for error messages
 * @param {String} message The message
 * @param {import('immutable').Map} [mediaAsset] An immmutable map that is an mediaAsset
 * @param {Error} [err] An optional error instance
 */
function createMediaAssetsMessageText (message, mediaAsset, err) {
  let msg = message
  if (mediaAsset && mediaAsset.get('originalFileName')) {
    msg += ` - mediaAsset ${mediaAsset.get('originalFileName')}`
  }
  if (err) {
    msg += ` - ${toString(err)}`
  }
  return msg
}

async function getMediaAssetsDataAssetHandler ({ store, action }) {
  const { config, auth, mediaAssets } = store.getState()
  const { storeKey } = action.payload
  let { asset = Map(), index } = action.payload
  if (!isFinite(index)) {
    index = mediaAssets.getIn([storeKey, 'data'], List()).size
  }
  store.dispatch(setMediaAssetsDataAsset({ asset: asset.set('processing', true), storeKey, index }))
  const mediaId = asset.get('mediaId')
  try {
    const media = await getMedia({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      mediaId,
    })
    asset = asset.set('media', media)
  } catch (err) {
    store.dispatch(setMediaAssetsMessage({
      storeKey,
      message: createMediaAssetsMessageText(`The media ${mediaId} does not exist. The media asset will be created, but cannot find a media match.`, asset, err),
    }))
  }
  const contentId = asset.getIn(['media', 'contentId'])
  try {
    asset = asset.set('currentAsset', null)
    const currentAssets = await getMediaAssetsByMediaId({
      mediaId,
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
    })
    const language = asset.get('language')
    const currentAsset = currentAssets.find((possibleAsset) => possibleAsset.get('language') === language)
    asset = asset.set('currentAsset', currentAsset)
  } catch (err) {
    store.dispatch(setMediaAssetsDataAssetProcessing({ processing: false, storeKey, index }))
    if (err.status !== 404) {
      store.dispatch(setMediaAssetsMessage({
        storeKey,
        message: createMediaAssetsMessageText(`Failed to get media assets for content id ${contentId}`, asset, err),
      }))
      return
    }
  }
  store.dispatch(setMediaAssetsDataAsset({ asset: asset.delete('processing'), storeKey, index }))
}

async function getMediaAssetByIdDataHandler ({ store, action }) {
  const { config, auth, mediaAssets } = store.getState()
  const index = mediaAssets.getIn([storeKey, 'data'], List()).size
  const { payload: { id, storeKey } = {} } = action
  try {
    store.dispatch(setMediaAssetsDataAssetProcessing({ processing: true, storeKey, index }))
    const asset = await getMediaAssetById({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      id,
    })
    store.dispatch(setMediaAssetsDataAsset({
      asset: Map({ currentAsset: asset, processing: false }),
      storeKey,
    }))
  } catch (err) {
    store.dispatch(setMediaAssetsDataAssetProcessing({ processing: false, storeKey, index }))
    store.dispatch(setMediaAssetsMessage({
      storeKey,
      message: createMediaAssetsMessageText(`Failed to get media asset data for ${id}`, undefined, err),
    }))
  }
}

async function publishMediaAssetsDataHandler ({ store, action }) {
  const { data, storeKey } = action.payload
  data.forEach(
    (asset, index) => store.dispatch(publishMediaAssetsDataAsset({ asset, storeKey, index })),
  )
}

async function publishMediaAssetsDataAssetHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { payload: { storeKey, index } = {} } = action
  const { payload: { asset } = {} } = action
  // Mark this media asset as publishing, when we return the payload
  store.dispatch(setMediaAssetsDataAsset({
    asset: asset.set('publishing', true).set('processing', true),
    storeKey,
    index,
  }))

  const MAX_RETRIES = 10

  /**
   * Publish a media asset with retry using the API
   * @param {import('immutable').Map} data The media asset from the store
   * @param {Number} [attempt=0] The current attempt
   */
  async function publishStep (data, attempt) {
    try {
      return await upsertMediaAsset({
        data: data.set('provider', MEDIA_ASSETS_PROVIDER_AWS_S3),
        auth,
        server: config.getIn(['data', 'servers', 'brooklyn']),
      })
    } catch (err) {
      if (attempt < MAX_RETRIES) {
        await delay(100)
        return publishStep(data, attempt + 1)
      }
      store.dispatch(setMediaAssetsMessage({
        storeKey,
        message: createMediaAssetsMessageText('Failed to publish', data, err),
      }))
      throw err
    }
  }

  // If currently have a media asset delete that one before creating a new one
  try {
    const currentAsset = await publishStep(asset)
    store.dispatch(setMediaAssetsDataAsset({
      asset: asset.delete('publishing').delete('processing').set('currentAsset', currentAsset),
      storeKey,
      index,
    }))
  } catch (err) {
    store.dispatch(setMediaAssetsDataAssetProcessing({ processing: false, storeKey, index }))
  }
}

async function unpublishMediaAssetsDataAssetHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { asset, storeKey, index } = action.payload
  store.dispatch(setMediaAssetsDataAsset({
    asset: asset.set('unpublishing', true).set('processing', true),
    storeKey,
    index,
  }))
  try {
    await deleteMediaAsset({
      data: asset.get('currentAsset'),
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
    })
  } catch (err) {
    store.dispatch(setMediaAssetsDataAssetProcessing({ processing: false, storeKey, index }))
    store.dispatch(setMediaAssetsMessage({
      storeKey,
      message: createMediaAssetsMessageText('Failed to delete', asset, err),
    }))
    // Don't throw, this function lives in a redux middleware
    return
  }
  store.dispatch(setMediaAssetsDataAsset({
    asset: asset.delete('currentAsset').delete('unpublishing').delete('processing'),
    storeKey,
    index,
  }))
}

// async function reloadMediaAssetsDataAssetHandler ({ store, action }) {
//  const { mediaAssets } = store.getState()
//  const { storeKey } = action.payload
//  const data = mediaAssets.getIn([storeKey, 'data']) || List()
//  data.forEach((asset, index) =>
//  store.dispatch(getMediaAssetsDataAsset({ asset, storeKey, index })))
// }

export default (store) => (next) => (action) => {
  const { type } = action
  switch (type) {
    case GET_MEDIA_ASSETS_DATA_ASSET:
      getMediaAssetsDataAssetHandler({ store, action })
      break
    case PUBLISH_MEDIA_ASSETS_DATA:
      publishMediaAssetsDataHandler({ store, action })
      break
    case PUBLISH_MEDIA_ASSETS_DATA_ASSET:
      publishMediaAssetsDataAssetHandler({ store, action })
      break
    case UNPUBLISH_MEDIA_ASSETS_DATA_ASSET:
      unpublishMediaAssetsDataAssetHandler({ store, action })
      break
    case GET_MEDIA_ASSET_BY_ID:
      getMediaAssetByIdDataHandler({ store, action })
      break
    default:
      break
  }
  next(action)
}
